import React, { useContext, useRef, useState, useEffect } from "react";
import { createPortal } from "react-dom";
import "./home.css";
import { confirmDialog } from 'primereact/confirmdialog';
import { AuthContext } from "../../Header/auth - context.js";
import { Toast } from "primereact/toast";
import { Button } from "primereact/button";
import { InputTextarea } from "primereact/inputtextarea";
import { Dropdown } from "primereact/dropdown";
import { MultiSelect } from "primereact/multiselect";
import { Dialog } from 'primereact/dialog';
import PlanFooter from "./plan_footer";
import { uzmiGresku } from "../../custom/greske";
import { useDispatch } from "react-redux";
import { ChatSocketContext } from "../../context/ChatSocketContext";
import { useTranslation } from "react-i18next";

const PRIORITET_COLOR = { 1: '#e53935', 2: '#fb8c00', 3: '#43a047' };

const Plan = (props) => {
  const { t } = useTranslation();
  const toast = useRef(null);
  const auth  = useContext(AuthContext);
  const fileInputRef = useRef(null);
  const commentFileInputRef = useRef(null);
  const commentsEndRef = useRef(null);

  const assigneeIds = (props.plan.AssigneeIds || String(props.plan.Task_za || '')).split(',').map(Number).filter(Boolean);

  /* Autor može biti i mobilni nalog (task prijavljen sa telefona). Mobilni nalozi
     imaju svoj brojač, pa operater sa istim brojem NIJE autor — bez provere tipa bi
     dobio pravo da menja i briše tuđi task. Backend to sada i sam odbija. */
  const autorJeOperater = (props.plan.Task_od_tip || 'OPERATER') === 'OPERATER';
  const isCreator  = autorJeOperater && props.plan.Task_od === props.currentUserId;
  const isAdmin    = props.userRoleId === 1;
  const isAssignee = assigneeIds.includes(props.currentUserId) || props.plan.Task_za === props.currentUserId;
  const canEdit       = isCreator || isAdmin;
  const canEditStatus = !canEdit && isAssignee;
  const canDelete     = isCreator || isAdmin;
  const canComment    = isCreator || isAssignee || isAdmin;

  const [modalOpen,       setModalOpen]       = useState(false);
  const [statusOnlyEdit,  setStatusOnlyEdit]  = useState(false);
  /* Zadatak koji nije ni moj ni meni zadat — otvara se samo da se pročita. */
  const [samoPregled,     setSamoPregled]     = useState(false);
  const [toggleText,      setToggleText]      = useState(props.text);
  const [stateTicketDrop, setStateTicketDrop] = useState(props.plan.Stanje);
  const [userDrop,        setUserDrop]        = useState(assigneeIds);
  const [prioritetDrop,   setPrioritetDrop]   = useState(props.plan.Prioritet);
  const [gradient,        setGradient]        = useState(props.text.length > 95);
  const [expanded,        setExpanded]        = useState(false);
  const [isDragging,      setIsDragging]      = useState(false);

  // Comment modal
  const [commentsModalOpen, setCommentsModalOpen] = useState(false);
  const [neprocitanoSakriveno, setNeprocitanoSakriveno] = useState(false);
  const [comments,       setComments]       = useState([]);
  const [commentsLoaded, setCommentsLoaded] = useState(false);
  const [newComment,     setNewComment]     = useState('');
  const [replyTo,        setReplyTo]        = useState(null);
  const [editingId,      setEditingId]      = useState(null);
  const [editingText,    setEditingText]    = useState('');
  // Bug 1: initialize from backend KomentariCount, not from loaded comments array
  const [commentCount,   setCommentCount]   = useState(props.plan.KomentariCount || 0);

  // Image state
  const [planSlike,    setPlanSlike]    = useState([]);
  const [slikeLoaded,  setSlikeLoaded]  = useState(false);
  const [pendingSlike, setPendingSlike] = useState([]); // awaiting upload in modal
  const [commentSlike, setCommentSlike] = useState([]); // images attached to current comment
  const [lightbox,     setLightbox]     = useState(null); // { images: [src], index } — pregled slika

  const thisDate   = new Date(props.date);
  const newPreview = thisDate.toLocaleDateString('sr-RS', { day: '2-digit', month: '2-digit', year: 'numeric' });
  const prioritetColor = PRIORITET_COLOR[props.plan.Prioritet] || '#90a4ae';
  const stanje = props.plan.Stanje;
  // Bug 5: removing !commentsOpen allows dragging even when comments are open
  const canDrag = !modalOpen;

  const handleErrors = (response) => {
    if (response.ok) return response.json();
    throw response;
  };

  const authHeaders = () => ({ 'Content-Type': 'application/json', Authorization: 'Bearer ' + auth.token });

  const dispatch = useDispatch();
  const chatSocket = useContext(ChatSocketContext);

  /* Posle citanja komentara oznaka mora da nestane na OBA mesta: broj na kartici
     dolazi iz upita za tablu, a brojac u zaglavlju iz /plan/novi_count — pa se
     osvezavaju zajedno, inace jedno ostane da visi. */
  /* Ponovno učitavanje komentara — koristi se i pri otvaranju i kad stigne
     javljanje da je neko pročitao ili dopisao. */
  const ucitajKomentare = () =>
    fetch(process.env.REACT_APP_BACKEND_URL + '/plan/komentari/list', {
      method: 'POST', headers: authHeaders(),
      body: JSON.stringify({ plan_id: props.ID }),
    })
      .then(handleErrors)
      .then(k => setComments(k))
      .catch(e => uzmiGresku(e, t('plan.greskaKomentari')).then(m =>
        toast.current?.show({ severity: 'error', summary: t('plan.greskaKomentari'), detail: m.message, life: 5000 })));

  /* Tabla čeka na zatvaranje prozora.

     `/plan` vraća završen zadatak stariji od mesec dana SAMO dok na njemu ima
     nepročitan komentar. Otvaranje komentara upisuje da su pročitani, pa bi
     osvežavanje u tom trenutku izbacilo karticu iz liste — React bi odmontirao
     komponentu, a s njom i prozor koji je upravo otvoren. Korisniku to izgleda
     kao da se zadatak sam zatvorio.

     Brojači u zaglavlju se osvežavaju ODMAH (oni ne diraju listu), a sama tabla
     tek kad se prozor zatvori. */
  const tablaCeka = useRef(false);

  const osveziTabluAkoTreba = () => {
    if (!tablaCeka.current) return;
    tablaCeka.current = false;
    props.render?.();
  };

  const osveziNeprocitane = () => {
    tablaCeka.current = true;
    /* Oznaka nepročitanog se gasi odmah, lokalno: podatak za nju stiže kroz
       props sa table, a tabla se osvežava tek kasnije. */
    setNeprocitanoSakriveno(true);
    fetch(process.env.REACT_APP_BACKEND_URL + '/plan/novi_count', {
      method: 'POST', headers: authHeaders(),
    })
      .then(r => r.ok ? r.json() : null)
      .then(d => {
        if (!d) return;
        dispatch({ type: 'PLAN_NOVI_SET', value: d.count || 0 });
        dispatch({ type: 'PLAN_NEPROCITANI_SET', value: d.neprocitani || 0 });
      })
      .catch(() => {});
  };

  /* Lightbox za pregled slika sa navigacijom levo/desno + X (kao u chatu). `lightbox` = { images:[src], index }.
     Slike su base64 data: URI — <img src> ih prikazuje direktno (za razliku od window.open(data:) koji browser blokira). */
  const openLightbox = (images, index) => { if (images?.length) setLightbox({ images, index }); };
  const closeLightbox = () => setLightbox(null);
  const lightboxPrev = () => setLightbox(lb => (lb && lb.index > 0) ? { ...lb, index: lb.index - 1 } : lb);
  const lightboxNext = () => setLightbox(lb => (lb && lb.index < lb.images.length - 1) ? { ...lb, index: lb.index + 1 } : lb);

  const footerDataSort = () => {
    let TaskOd = '', Prioritet = '', Warn = false, LastEditedBy = '';
    const TaskZaList = assigneeIds.map(id => props.users.find(u => u.value === id)?.label).filter(Boolean);
    for (const u of props.users) {
      if (autorJeOperater && u.value === props.plan.Task_od) TaskOd = u.label;
      if (u.value === props.plan.LastEditedBy) LastEditedBy = u.label;
    }
    /* Imena koja je razrešio server znaju tip — za mobilnog autora/izmenioca lista
       operatera bi po istom broju dala tuđe ime. */
    if (!autorJeOperater) TaskOd = props.plan.AutorIme ? `${props.plan.AutorIme} (${t('taskovi.saTelefona')})` : TaskOd;
    if (props.plan.IzmenioIme) LastEditedBy = props.plan.IzmenioIme;
    for (const p of props.prioritet) {
      if (p.value === props.plan.Prioritet) { Prioritet = p.label; if (p.value === 1) Warn = true; }
    }
    return [TaskOd, TaskZaList, Prioritet, Warn, LastEditedBy];
  };

  const lastEditedInfo = () => {
    if (!props.plan.DateEditedDisplay || !props.plan.LastEditedBy) return null;
    const [, , , , lastEditedByName] = footerDataSort();
    return lastEditedByName ? `${lastEditedByName}, ${props.plan.DateEditedDisplay}` : null;
  };

  useEffect(() => {
    const found = props.planAll.find(e => e.id === props.ID);
    if (found) setToggleText(found.Text);
  }, [props.ID]);

  // Tastatura u lightbox-u: ←/→ navigacija, Esc zatvara
  useEffect(() => {
    if (!lightbox) return;
    const onKey = (e) => {
      if (e.key === 'ArrowLeft') lightboxPrev();
      else if (e.key === 'ArrowRight') lightboxNext();
      else if (e.key === 'Escape') closeLightbox();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [lightbox]); // eslint-disable-line

  // ── Image helpers ───────────────────────────────────────────
  const readFileAsDataUrl = (file) => new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = (e) => resolve(e.target.result);
    reader.readAsDataURL(file);
  });

  const ensureDataLoaded = () => {
    if (commentsLoaded) return Promise.resolve();
    return Promise.all([
      fetch(process.env.REACT_APP_BACKEND_URL + '/plan/komentari/list', {
        method: 'POST', headers: authHeaders(),
        body: JSON.stringify({ plan_id: props.ID }),
      }).then(handleErrors),
      fetch(process.env.REACT_APP_BACKEND_URL + '/plan/slike/list', {
        method: 'POST', headers: authHeaders(),
        body: JSON.stringify({ plan_id: props.ID }),
      }).then(r => r.ok ? r.json() : []).catch(() => []),
    ]).then(([komentari, slike]) => {
      setComments(komentari);
      setPlanSlike(slike);
      setSlikeLoaded(true);
      setCommentsLoaded(true);
      /* Backend je upravo upisao da je korisnik video komentare, pa oznaka
         nepročitanog na kartici i brojač u zaglavlju moraju odmah da se sklone.
         Bez ovoga bi stajali do sledećeg osvežavanja table. */
      osveziNeprocitane();
    })
      /* Greska se PROSLEDJUJE dalje, ne guta.

         Ranije je ovde stajalo `.catch(() => {})`. Posledica nije bila samo da
         se greska ne vidi — `loadComments` radi `ensureDataLoaded().then(...)`,
         pa se obecanje USPESNO razresilo i prozor se otvarao PRAZAN. Kvar je
         tako izgledao identicno kao "nema komentara", i to na ekranu na kom
         kartica istovremeno pokazuje da ih ima tri.

         Sada greska stigne do korisnika, a prozor se ne otvara — jer `then`
         koji ga otvara vise ne dolazi na red. */
      .catch(e => uzmiGresku(e, t('plan.greskaKomentari')).then(m => {
        toast.current?.show({ severity: 'error', summary: t('plan.greskaKomentari'), detail: m.message, life: 5000 });
        throw e;
      }));
  };

  /* Dok su komentari učitani, prati javljanja sa servera: nov komentar i to da
     je neko pročitao. Bez ovoga oznaka "Pročitano" stoji onakva kakva je bila
     kad je prozor otvoren — a upravo to je i bila zamerka. */
  useEffect(() => {
    const socket = chatSocket?.socketRef?.current;
    if (!socket || !commentsLoaded) return;

    const naDogadjaj = (d) => {
      if (Number(d?.plan_id) !== Number(props.ID)) return;
      ucitajKomentare();
      osveziNeprocitane();
    };

    socket.on('plan:comment', naDogadjaj);
    socket.on('plan:seen', naDogadjaj);
    return () => {
      socket.off('plan:comment', naDogadjaj);
      socket.off('plan:seen', naDogadjaj);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [commentsLoaded, props.ID]);

  const uploadSlikaToBackend = (plan_id, komentar_id, slika, filename) =>
    fetch(process.env.REACT_APP_BACKEND_URL + '/plan/slike/add', {
      method: 'POST', headers: authHeaders(),
      body: JSON.stringify({ plan_id, komentar_id, slika, filename }),
    }).then(r => r.json()).catch(() => null);

  const handleModalPaste = async (e) => {
    const items = e.clipboardData?.items;
    if (!items) return;
    for (const item of items) {
      if (item.type.startsWith('image/')) {
        const file = item.getAsFile();
        const data = await readFileAsDataUrl(file);
        setPendingSlike(prev => [...prev, { data, filename: file.name || 'paste.png' }]);
      }
    }
  };

  const handleModalFileSelect = async (e) => {
    for (const file of Array.from(e.target.files)) {
      const data = await readFileAsDataUrl(file);
      setPendingSlike(prev => [...prev, { data, filename: file.name }]);
    }
    e.target.value = '';
  };

  const handleCommentPaste = async (e) => {
    const items = e.clipboardData?.items;
    if (!items) return;
    for (const item of items) {
      if (item.type.startsWith('image/')) {
        const file = item.getAsFile();
        const data = await readFileAsDataUrl(file);
        setCommentSlike(prev => [...prev, { data, filename: file.name || 'paste.png' }]);
      }
    }
  };

  const handleCommentFileSelect = async (e) => {
    for (const file of Array.from(e.target.files)) {
      const data = await readFileAsDataUrl(file);
      setCommentSlike(prev => [...prev, { data, filename: file.name }]);
    }
    e.target.value = '';
  };

  const deletePlanSlika = (id) => {
    fetch(process.env.REACT_APP_BACKEND_URL + '/plan/slike/delete', {
      method: 'PATCH', headers: authHeaders(),
      body: JSON.stringify({ id }),
    }).then(handleErrors).then(() => { setPlanSlike(prev => prev.filter(s => s.id !== id)); props.render(); }).catch(() => {});
  };

  // ── Edit (modal) ─────────────────────────────────────────────
  const openEdit = () => { setModalOpen(true); setStatusOnlyEdit(false); setSamoPregled(false); ensureDataLoaded(); };
  const openStatusEdit = () => { setModalOpen(true); setStatusOnlyEdit(true); setSamoPregled(false); ensureDataLoaded(); };

  /* Pregled tuđeg zadatka. Komentari se NE učitavaju: tekst zadatka onaj ko
     gleda tablu ionako vidi na kartici, a razgovor učesnika je njihov — ko
     nije učesnik nema dugme za komentare ni na kartici. */
  const openView = () => {
    setModalOpen(true); setStatusOnlyEdit(true); setSamoPregled(true);
    if (canComment) ensureDataLoaded();
  };

  /* Dvoklik na karticu otvara zadatak u obimu koji korisnik sme. Dugmad na
     kartici ostaju — ovo je prečica, ne zamena. */
  const dvoklikNaKarticu = () => {
    if (canEdit) openEdit();
    else if (canEditStatus) openStatusEdit();
    else openView();
  };

  const closeModal = () => {
    setModalOpen(false); setStatusOnlyEdit(false); setSamoPregled(false); setPendingSlike([]);
    setReplyTo(null); setNewComment(''); setCommentSlike([]); setEditingId(null);
    osveziTabluAkoTreba();
  };

  const saveEdit = async () => {
    const textNew = toggleText;
    if (!props.ID || textNew.length < 2) {
      toast.current.show({ severity: 'warn', summary: 'Upozorenje!', detail: 'Tekst mora biti veći od 2 karaktera!', life: 3000 });
      return;
    }
    if (!statusOnlyEdit && !userDrop?.length) {
      toast.current.show({ severity: 'warn', summary: 'Upozorenje!', detail: 'Izaberite bar jednog korisnika!', life: 3000 });
      return;
    }

    const sameAssignees = userDrop.length === assigneeIds.length
      && [...userDrop].sort().join(',') === [...assigneeIds].sort().join(',');
    const hasChanges = props.text !== textNew || props.plan.Stanje !== stateTicketDrop || !sameAssignees || props.plan.Prioritet !== prioritetDrop;

    if (hasChanges) {
      try {
        const data = await fetch(process.env.REACT_APP_BACKEND_URL + '/plan', {
          method: 'PATCH', headers: authHeaders(),
          body: JSON.stringify({ id: props.ID, text: textNew, state: stateTicketDrop, Task_za: userDrop, prioritet: prioritetDrop }),
        }).then(handleErrors);

        if (data.message === 'Stari plan je isti kao novi plan!') {
          toast.current.show({ severity: 'info', summary: 'Info', detail: data.message, life: 3000 });
          return;
        }

        for (const img of pendingSlike) {
          const r = await uploadSlikaToBackend(props.ID, null, img.data, img.filename);
          if (r?.id) setPlanSlike(prev => [...prev, { id: r.id, plan_id: props.ID, komentar_id: null, filename: img.filename, slika: img.data }]);
        }

        setToggleText(textNew);
        if (textNew.length > 95) setGradient(true);
        toast.current.show({ severity: 'success', summary: 'Task uspešno ažuriran!', life: 3000 });
        closeModal();
        props.render();
      } catch (error) {
        error.json?.().then(m => toast.current.show({ severity: 'error', summary: 'Error', detail: m.message, life: 3000 }));
      }
    } else if (pendingSlike.length > 0) {
      for (const img of pendingSlike) {
        const r = await uploadSlikaToBackend(props.ID, null, img.data, img.filename);
        if (r?.id) setPlanSlike(prev => [...prev, { id: r.id, plan_id: props.ID, komentar_id: null, filename: img.filename, slika: img.data }]);
      }
      toast.current.show({ severity: 'success', summary: 'Slike su dodate!', life: 3000 });
      closeModal();
      props.render();   // osveži bord da se SlikeCount badge na kartici ažurira (npr. 2. slika → badge 2)
    } else {
      toast.current.show({ severity: 'info', summary: 'Info', detail: 'Nema izmena.', life: 3000 });
    }
  };

  // ── Drag ─────────────────────────────────────────────────────
  const handleDragStart = (e) => {
    if (e.target.closest('.comment-thread')) { e.preventDefault(); return; }
    setIsDragging(true);
    e.dataTransfer.setData('planId', String(props.ID));
    e.dataTransfer.setData('sourceStanje', String(stanje));
    e.dataTransfer.effectAllowed = 'move';
  };

  // ── Delete ────────────────────────────────────────────────────
  const handleDelete = () => {
    if (!canDelete) {
      toast.current.show({ severity: 'warn', summary: 'Nije dozvoljeno', detail: 'Samo kreator zadatka može da ga obriše!', life: 4000 });
      return;
    }
    confirmDialog({
      message: 'Jel ste sigurni da želite obrisati ovaj zadatak?',
      header: 'Potvrda brisanja',
      icon: 'pi pi-exclamation-triangle',
      acceptClassName: 'p-button-danger',
      acceptLabel: 'Da, obriši',
      rejectLabel: 'Ne',
      accept: () => {
        fetch(process.env.REACT_APP_BACKEND_URL + '/plan/deletePlan', {
          method: 'PATCH', headers: authHeaders(),
          body: JSON.stringify({ id: props.plan.id }),
        })
          .then(r => r.json())
          .then(() => {
            const newPlanDB = props.planAll.filter((_, idx) => idx !== props.index);
            props.setPlan({ type: props.dbKey, value: newPlanDB });
          })
          .catch(error => error.json?.().then(m => toast.current.show({ severity: 'error', summary: 'Error', detail: m.message, life: 3000 })));
      },
    });
  };

  // ── Comments ──────────────────────────────────────────────────
  const loadComments = () => {
    if (commentsLoaded) { setCommentsModalOpen(true); return; }
    /* `catch` je obavezan iako poruku vec prikazuje `ensureDataLoaded`:
       bez njega odbijeno obecanje zavrsi kao neuhvacena greska u konzoli. */
    ensureDataLoaded().then(() => setCommentsModalOpen(true)).catch(() => {});
  };

  useEffect(() => {
    if ((commentsModalOpen || modalOpen) && commentsEndRef.current) {
      setTimeout(() => commentsEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 120);
    }
  }, [commentsModalOpen, modalOpen, comments.length]);

  const submitComment = async () => {
    if (!newComment.trim()) return;
    try {
      const data = await fetch(process.env.REACT_APP_BACKEND_URL + '/plan/komentari/add', {
        method: 'POST', headers: authHeaders(),
        body: JSON.stringify({ plan_id: props.ID, tekst: newComment.trim(), parent_id: replyTo?.id || null }),
      }).then(handleErrors);

      setComments(prev => [...prev, data]);
      setCommentCount(prev => prev + 1);
      setNewComment('');
      setReplyTo(null);

      for (const img of commentSlike) {
        const r = await uploadSlikaToBackend(props.ID, data.id, img.data, img.filename);
        if (r?.id) setPlanSlike(prev => [...prev, { id: r.id, plan_id: props.ID, komentar_id: data.id, filename: img.filename, slika: img.data }]);
      }
      setCommentSlike([]);
    } catch (err) {
      err.json?.().then(m => toast.current.show({ severity: 'error', summary: 'Error', detail: m.message, life: 3000 }));
    }
  };

  const submitEdit = (id) => {
    if (!editingText.trim()) return;
    fetch(process.env.REACT_APP_BACKEND_URL + '/plan/komentari/edit', {
      method: 'PATCH', headers: authHeaders(),
      body: JSON.stringify({ id, tekst: editingText.trim() }),
    })
      .then(handleErrors)
      .then(() => {
        setComments(prev => prev.map(c => c.id === id ? { ...c, tekst: editingText.trim(), edited: true } : c));
        setEditingId(null); setEditingText('');
      })
      .catch(err => err.json?.().then(m => toast.current.show({ severity: 'error', summary: 'Error', detail: m.message, life: 3000 })));
  };

  const handleDeleteComment = (id) => {
    fetch(process.env.REACT_APP_BACKEND_URL + '/plan/komentari/delete', {
      method: 'PATCH', headers: authHeaders(),
      body: JSON.stringify({ id }),
    })
      .then(handleErrors)
      .then(() => {
        setComments(prev => prev.filter(c => c.id !== id));
        setCommentCount(prev => Math.max(0, prev - 1));
      })
      .catch(err => err.json?.().then(m => toast.current.show({ severity: 'error', summary: 'Error', detail: m.message, life: 3000 })));
  };

  const toggleExpand = () => {
    if (!modalOpen) {
      setGradient(expanded && toggleText.length > 95);
      setExpanded(e => !e);
    }
  };

  const [od, za, prioritetLabel, warn] = footerDataSort();
  const lastEdited = lastEditedInfo();
  const taskSlike = planSlike.filter(s => !s.komentar_id);
  const taskImages = taskSlike.map(s => s.slika);

  const editDialogFooter = (
    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
      {/* U pregledu nema šta da se sačuva, pa stoji samo zatvaranje. */}
      {!samoPregled && (
        <Button onClick={saveEdit} label="Sačuvaj" icon="pi pi-check" className="p-button-success" />
      )}
      <Button onClick={closeModal} label={samoPregled ? t('common.close') : 'Otkaži'}
              icon="pi pi-times" className="p-button-secondary" />
    </div>
  );

  return (
    <div
      className={`card${expanded ? ' card-expanded' : ''}${isDragging ? ' card-dragging' : ''}`}
      style={{ borderLeft: `4px solid ${prioritetColor}`, opacity: isDragging ? 0.45 : 1 }}
      draggable={canDrag}
      onDragStart={canDrag ? handleDragStart : undefined}
      onDragEnd={() => setIsDragging(false)}
      onDoubleClick={dvoklikNaKarticu}
    >
      <Toast ref={toast} />

      {/* ── Lightbox pregled slika — kroz PORTAL na document.body da izađe iz stacking konteksta
             kolona/kartica (transform/overflow), inače ga UI kolona prekrije uprkos z-index-u ── */}
      {lightbox && createPortal(
        <div className="plan-img-overlay" onClick={closeLightbox}>
          <button className="plan-img-overlay-close" onClick={closeLightbox} title="Zatvori">×</button>
          {lightbox.index > 0 && (
            <button className="plan-img-overlay-nav plan-img-overlay-prev"
              onClick={(e) => { e.stopPropagation(); lightboxPrev(); }} title="Prethodna">❮</button>
          )}
          <img src={lightbox.images[lightbox.index]} alt="" className="plan-img-overlay-img"
            onClick={(e) => e.stopPropagation()} />
          {lightbox.index < lightbox.images.length - 1 && (
            <button className="plan-img-overlay-nav plan-img-overlay-next"
              onClick={(e) => { e.stopPropagation(); lightboxNext(); }} title="Sledeća">❯</button>
          )}
          {lightbox.images.length > 1 && (
            <div className="plan-img-overlay-count" onClick={(e) => e.stopPropagation()}>
              {lightbox.index + 1} / {lightbox.images.length}
            </div>
          )}
        </div>,
        document.body
      )}

      {/* ── Header ── */}
      <div className="card-headerr" style={{ cursor: canDrag ? 'grab' : 'default' }}>
        <div className="card-header-meta" onClick={toggleExpand} style={{ cursor: toggleText.length > 95 ? 'pointer' : 'default' }}>
          <span className="card-id">#{props.ID}</span>
          <span className="card-date">{newPreview}</span>
          {stanje === 3 && <span className="card-badge done">Završeno</span>}
        </div>
        <div id="btnPlan">
          {/* Neprocitani komentari — crveni broj, da se na tabli odmah vidi gde
              te neko ceka. Gasi se cim se komentari otvore (backend tada upise
              trenutak citanja). */}
          {Number(props.plan.NeprocitaniCount) > 0 && !neprocitanoSakriveno && (
            <span className="plan-unread-indicator"
                  title={`Nepročitanih komentara: ${props.plan.NeprocitaniCount}`}>
              <i className="pi pi-comment" />
              <span className="plan-unread-count">{props.plan.NeprocitaniCount}</span>
            </span>
          )}
          {Number(props.plan.SlikeCount) > 0 && (
            <span className="plan-attach-indicator" title={`Prilozi: ${props.plan.SlikeCount}`}>
              <i className="pi pi-paperclip" />
              <span className="plan-attach-count">{props.plan.SlikeCount}</span>
            </span>
          )}
          {canEdit && (
            <Button onClick={openEdit} className="p-button-sm p-button-raised p-button-primary" icon="pi pi-pencil" tooltip="Izmeni" tooltipOptions={{ position: 'top' }} />
          )}
          {canEditStatus && (
            <Button onClick={openStatusEdit} className="p-button-sm p-button-raised p-button-primary" icon="pi pi-pencil" tooltip="Promeni status" tooltipOptions={{ position: 'top' }} />
          )}
          {canComment && (
            <Button
              onClick={loadComments}
              className="p-button-sm p-button-raised p-button-secondary"
              icon="pi pi-comments"
              badge={commentCount > 0 ? String(commentCount) : undefined}
              badgeClassName="p-badge-warning"
              tooltip="Komentari"
              tooltipOptions={{ position: 'top' }}
              style={{ minWidth: 'fit-content' }}
            />
          )}
          <Button onClick={handleDelete} icon="pi pi-trash" className="p-button-sm p-button-raised p-button-danger" tooltip="Obriši" tooltipOptions={{ position: 'top' }} />
        </div>
      </div>

      {/* ── Body — always read-only (Bug 3) ── */}
      <div
        className={`card-bodyPlan${!expanded && toggleText.length > 95 ? ' card-bodyPlan-gradiant' : ''}`}
        onClick={toggleText.length > 95 ? toggleExpand : undefined}
        style={toggleText.length > 95 ? { cursor: 'pointer' } : undefined}
      >
        <p className="card-text">{toggleText}</p>
      </div>

      {/* ── Edit Dialog (edit form + comments) ── */}
      <Dialog
        visible={modalOpen}
        onHide={closeModal}
        header={`Zadatak #${props.ID} — ${newPreview}`}
        footer={editDialogFooter}
        style={{ width: '60vw' }}
        breakpoints={{ '768px': '90vw' }}
        modal
        draggable={false}
        resizable={false}
      >
        <div className="plan-edit-dialog-body">

          {/* ── LEFT: edit form ── */}
          <div className="plan-edit-left">
            {/* Ko nije autor vidi tekst, ali ga ne menja.

                `readOnly`, ne `disabled`: onemogućeno polje u pregledačima ne
                dozvoljava ni označavanje ni kopiranje teksta, a upravo to je
                ono što ovde treba — pročitati i prekopirati, bez izmene.
                Izgled zaključanog polja nosi klasa, da se i dalje vidi da se
                ne menja. */}
            <InputTextarea
              style={{ width: '100%' }}
              className={statusOnlyEdit ? 'plan-polje-zakljucano' : undefined}
              autoResize
              value={toggleText}
              onChange={e => setToggleText(e.target.value)}
              onPaste={handleModalPaste}
              placeholder="Unesite plan"
              rows={4}
              readOnly={statusOnlyEdit}
            />
            <div style={{ display: 'flex', gap: '8px' }}>
              <Dropdown
                value={stateTicketDrop}
                options={props.stateTicketDB}
                onChange={e => setStateTicketDrop(e.value)}
                placeholder="Status"
                style={{ flex: 1 }}
                filter
                disabled={samoPregled}
              />
              <MultiSelect
                value={userDrop}
                options={props.users}
                onChange={e => setUserDrop(e.value)}
                placeholder="Za zaposlenog"
                style={{ flex: 2 }}
                display="chip"
                filter
                disabled={statusOnlyEdit}
              />
              <Dropdown
                value={prioritetDrop}
                options={props.prioritet}
                onChange={e => setPrioritetDrop(e.value)}
                placeholder="Prioritet"
                style={{ flex: 1 }}
                disabled={statusOnlyEdit}
              />
            </div>
            {/* Slike: u status-only modu prikazujemo postojeće slike (readonly), ali samo ako ih ima —
                bez dugmadi za dodavanje/brisanje (brisanje je ionako gejtovano sa canEdit) */}
            {(!statusOnlyEdit || taskSlike.length > 0) && (
              <>
                <div className="plan-modal-slike">
                  {taskSlike.map((s, i) => (
                    <div key={s.id} className="modal-slika-wrap">
                      <img src={s.slika} alt={s.filename || 'slika'} className="modal-slika-thumb" onClick={() => openLightbox(taskImages, i)} title={s.filename} />
                      {canEdit && <button className="modal-slika-del" onClick={() => deletePlanSlika(s.id)}>×</button>}
                    </div>
                  ))}
                  {pendingSlike.map((s, i) => (
                    <div key={`p${i}`} className="modal-slika-wrap modal-slika-pending">
                      <img src={s.data} alt={s.filename} className="modal-slika-thumb" />
                      <button className="modal-slika-del" onClick={() => setPendingSlike(prev => prev.filter((_, idx) => idx !== i))}>×</button>
                    </div>
                  ))}
                  {!statusOnlyEdit && (
                    <div className="modal-slika-add-row">
                      <input type="file" accept="image/*" multiple style={{ display: 'none' }} ref={fileInputRef} onChange={handleModalFileSelect} />
                      <button className="ct-img-btn" onClick={() => fileInputRef.current?.click()} title="Dodaj sliku">
                        <i className="pi pi-image" style={{ marginRight: '4px' }} /> Dodaj sliku
                      </button>
                      <span className="modal-paste-hint">ili Ctrl+V u tekst polje</span>
                    </div>
                  )}
                </div>
              </>
            )}
            {lastEdited && (
              <div className="plan-edit-lastedit" style={{ marginTop: '10px', fontSize: '12px', color: '#888' }}>
                <b>Poslednja izmena:</b> <i>{lastEdited}</i>
              </div>
            )}
          </div>

          {/* Razgovor je stvar učesnika zadatka. Ko zadatak samo gleda sa table
              nema ni dugme za komentare na kartici, pa ih ne vidi ni ovde —
              prozor tada ima samo levu stranu. */}
          {canComment && <div className="plan-edit-divider" />}

          {canComment && (
          <div className="plan-edit-right">
            <div className="plan-edit-right-title">
              <i className="pi pi-comments" style={{ marginRight: '6px' }} />
              Komentari {commentCount > 0 && <span className="plan-edit-comment-count">({commentCount})</span>}
            </div>
            <div className="chat-body plan-edit-chat-body">
              {!commentsLoaded && <p className="chat-empty">Učitavanje...</p>}
              {commentsLoaded && comments.length === 0 && <p className="chat-empty">Nema komentara. Budite prvi!</p>}
              {comments.map(c => {
                const cSlike = planSlike.filter(s => s.komentar_id === c.id);
                const isOwn = c.user_id === props.currentUserId;
                return (
                  <div key={c.id} className={`chat-item${isOwn ? ' chat-item-own' : ''}${c.parent_id ? ' chat-item-reply' : ''}`}>
                    <div className="chat-meta">
                      <span className="chat-author">{c.ime} {c.prezime}</span>
                      <span className="chat-date">{new Date(c.datum).toLocaleString('sr-RS', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
                      {c.edited && <span className="ct-edited">(izmenjeno)</span>}
                    </div>
                    {editingId === c.id ? (
                      <div className="ct-edit-form">
                        <InputTextarea value={editingText} onChange={e => setEditingText(e.target.value)} autoResize rows={2} style={{ width: '100%', fontSize: '13px' }} />
                        <div className="ct-edit-actions">
                          <button className="ct-btn ct-btn-save" onClick={() => submitEdit(c.id)}>Sačuvaj</button>
                          <button className="ct-btn ct-btn-cancel" onClick={() => { setEditingId(null); setEditingText(''); }}>Otkaži</button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <p className="chat-text">{c.tekst}</p>
                        {cSlike.length > 0 && (
                          <div className="ct-slike">
                            {cSlike.map((s, i) => (
                              <img key={s.id} src={s.slika} alt={s.filename || 'slika'} className="ct-slika-thumb" onClick={() => openLightbox(cSlike.map(x => x.slika), i)} title={s.filename} />
                            ))}
                          </div>
                        )}
                      </>
                    )}
                    {/* Oznaka pročitanosti — vidi je samo autor komentara; drugima je
                        nebitno ko je još video. Kao potvrda isporuke u čatu. */}
                    {isOwn && Number(c.UcesnikaBezAutora) > 0 && (
                      <div className={'ct-procitano' + (Number(c.Procitali) > 0 ? ' procitano' : '')}
                           title={Number(c.Procitali) > 0
                                    ? 'Pročitali: ' + (c.ProcitaliImena || '')
                                    : 'Još niko nije otvorio komentare posle ovog komentara'}>
                        <i className={Number(c.Procitali) > 0 ? 'pi pi-check-circle' : 'pi pi-circle'} />
                        {Number(c.Procitali) > 0
                          ? (Number(c.UcesnikaBezAutora) === 1
                              ? 'Pročitano'
                              : `Pročitano ${c.Procitali}/${c.UcesnikaBezAutora}`)
                          : 'Nepročitano'}
                      </div>
                    )}
                    {editingId !== c.id && (
                      <div className="ct-actions">
                        {isOwn && (
                          <>
                            <button className="ct-btn" onClick={() => { setEditingId(c.id); setEditingText(c.tekst); }}><i className="pi pi-pencil" /> Izmeni</button>
                            <button className="ct-btn ct-btn-del" onClick={() => handleDeleteComment(c.id)}><i className="pi pi-trash" /> Obriši</button>
                          </>
                        )}
                        {!isOwn && canComment && (
                          <button className="ct-btn" onClick={() => setReplyTo({ id: c.id, author: `${c.ime} ${c.prezime}` })}><i className="pi pi-reply" /> Odgovori</button>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
              <div ref={commentsEndRef} />
            </div>
            {canComment && (
              <div className="chat-input-area">
                {replyTo && (
                  <div className="ct-replying-to">
                    <i className="pi pi-reply" style={{ fontSize: '0.7rem' }} />
                    {' '}<b>{replyTo.author}</b>
                    <button className="ct-cancel-reply" onClick={() => setReplyTo(null)}>×</button>
                  </div>
                )}
                {commentSlike.length > 0 && (
                  <div className="ct-pending-slike">
                    {commentSlike.map((s, i) => (
                      <div key={i} className="ct-pending-slika">
                        <img src={s.data} alt={s.filename} className="ct-slika-thumb" />
                        <button className="ct-slika-del" onClick={() => setCommentSlike(prev => prev.filter((_, idx) => idx !== i))}>×</button>
                      </div>
                    ))}
                  </div>
                )}
                <div className="ct-input-row">
                  <InputTextarea
                    value={newComment}
                    onChange={e => setNewComment(e.target.value)}
                    onPaste={handleCommentPaste}
                    placeholder={replyTo ? `Odgovor za ${replyTo.author}... (Ctrl+V za sliku)` : 'Novi komentar... (Ctrl+V za sliku)'}
                    autoResize rows={2}
                    style={{ fontSize: '13px', flex: 1 }}
                    onKeyDown={e => { if (e.key === 'Enter' && e.ctrlKey) submitComment(); }}
                  />
                  <input type="file" accept="image/*" multiple style={{ display: 'none' }} ref={commentFileInputRef} onChange={handleCommentFileSelect} />
                  <button className="ct-img-btn" onClick={() => commentFileInputRef.current?.click()} title="Dodaj sliku"><i className="pi pi-image" /></button>
                  <button className="ct-img-btn" onClick={submitComment} title="Pošalji (Ctrl+Enter)"><i className="pi pi-send" /></button>
                </div>
              </div>
            )}
          </div>
          )}

        </div>
      </Dialog>

      {/* ── Comments-only Dialog ── */}
      <Dialog
        visible={commentsModalOpen}
        onHide={() => {
          setCommentsModalOpen(false); setReplyTo(null); setNewComment('');
          setCommentSlike([]); setEditingId(null);
          osveziTabluAkoTreba();
        }}
        header={`Komentari — Zadatak #${props.ID}`}
        style={{ width: '40vw' }}
        breakpoints={{ '768px': '90vw' }}
        modal
        draggable={false}
        resizable={false}
        className="comments-dialog"
      >
        {/* Scrollable chat area */}
        <div className="chat-body">
          {comments.length === 0 && <p className="chat-empty">Nema komentara. Budite prvi!</p>}
          {comments.map(c => {
            const cSlike = planSlike.filter(s => s.komentar_id === c.id);
            const isOwn = c.user_id === props.currentUserId;
            return (
              <div key={c.id} className={`chat-item${isOwn ? ' chat-item-own' : ''}${c.parent_id ? ' chat-item-reply' : ''}`}>
                <div className="chat-meta">
                  <span className="chat-author">{c.ime} {c.prezime}</span>
                  <span className="chat-date">{new Date(c.datum).toLocaleString('sr-RS', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
                  {c.edited && <span className="ct-edited">(izmenjeno)</span>}
                </div>
                {editingId === c.id ? (
                  <div className="ct-edit-form">
                    <InputTextarea value={editingText} onChange={e => setEditingText(e.target.value)} autoResize rows={2} style={{ width: '100%', fontSize: '13px' }} />
                    <div className="ct-edit-actions">
                      <button className="ct-btn ct-btn-save" onClick={() => submitEdit(c.id)}>Sačuvaj</button>
                      <button className="ct-btn ct-btn-cancel" onClick={() => { setEditingId(null); setEditingText(''); }}>Otkaži</button>
                    </div>
                  </div>
                ) : (
                  <>
                    <p className="chat-text">{c.tekst}</p>
                    {cSlike.length > 0 && (
                      <div className="ct-slike">
                        {cSlike.map((s, i) => (
                          <img key={s.id} src={s.slika} alt={s.filename || 'slika'} className="ct-slika-thumb" onClick={() => openLightbox(cSlike.map(x => x.slika), i)} title={s.filename} />
                        ))}
                      </div>
                    )}
                  </>
                )}
                {/* Oznaka pročitanosti — vidi je samo autor komentara; drugima je
                    nebitno ko je još video. Kao potvrda isporuke u čatu. */}
                {isOwn && Number(c.UcesnikaBezAutora) > 0 && (
                  <div className={'ct-procitano' + (Number(c.Procitali) > 0 ? ' procitano' : '')}
                       title={Number(c.Procitali) > 0
                                ? 'Pročitali: ' + (c.ProcitaliImena || '')
                                : 'Još niko nije otvorio komentare posle ovog komentara'}>
                    <i className={Number(c.Procitali) > 0 ? 'pi pi-check-circle' : 'pi pi-circle'} />
                    {Number(c.Procitali) > 0
                      ? (Number(c.UcesnikaBezAutora) === 1
                          ? 'Pročitano'
                          : `Pročitano ${c.Procitali}/${c.UcesnikaBezAutora}`)
                      : 'Nepročitano'}
                  </div>
                )}
                {editingId !== c.id && (
                  <div className="ct-actions">
                    {isOwn && (
                      <>
                        <button className="ct-btn" onClick={() => { setEditingId(c.id); setEditingText(c.tekst); }}><i className="pi pi-pencil" /> Izmeni</button>
                        <button className="ct-btn ct-btn-del" onClick={() => handleDeleteComment(c.id)}><i className="pi pi-trash" /> Obriši</button>
                      </>
                    )}
                    {!isOwn && canComment && (
                      <button className="ct-btn" onClick={() => setReplyTo({ id: c.id, author: `${c.ime} ${c.prezime}` })}><i className="pi pi-reply" /> Odgovori</button>
                    )}
                  </div>
                )}
              </div>
            );
          })}
          <div ref={commentsEndRef} />
        </div>

        {/* Input area */}
        {canComment && (
          <div className="chat-input-area">
            {replyTo && (
              <div className="ct-replying-to">
                <i className="pi pi-reply" style={{ fontSize: '0.7rem' }} />
                {' '}<b>{replyTo.author}</b>
                <button className="ct-cancel-reply" onClick={() => setReplyTo(null)}>×</button>
              </div>
            )}
            {commentSlike.length > 0 && (
              <div className="ct-pending-slike">
                {commentSlike.map((s, i) => (
                  <div key={i} className="ct-pending-slika">
                    <img src={s.data} alt={s.filename} className="ct-slika-thumb" />
                    <button className="ct-slika-del" onClick={() => setCommentSlike(prev => prev.filter((_, idx) => idx !== i))}>×</button>
                  </div>
                ))}
              </div>
            )}
            <div className="ct-input-row">
              <InputTextarea
                value={newComment}
                onChange={e => setNewComment(e.target.value)}
                onPaste={handleCommentPaste}
                placeholder={replyTo ? `Odgovor za ${replyTo.author}... (Ctrl+V za sliku)` : 'Novi komentar... (Ctrl+V za sliku)'}
                autoResize rows={2}
                style={{ fontSize: '13px', flex: 1 }}
                onKeyDown={e => { if (e.key === 'Enter' && e.ctrlKey) submitComment(); }}
              />
              <input type="file" accept="image/*" multiple style={{ display: 'none' }} ref={commentFileInputRef} onChange={handleCommentFileSelect} />
              <button className="ct-img-btn" onClick={() => commentFileInputRef.current?.click()} title="Dodaj sliku"><i className="pi pi-image" /></button>
              <button className="ct-img-btn" onClick={submitComment} title="Pošalji (Ctrl+Enter)"><i className="pi pi-send" /></button>
            </div>
          </div>
        )}
      </Dialog>

      <PlanFooter operaterOd={od} operaterZa={za} warn={warn} prioritet={prioritetLabel} />
    </div>
  );
};

export default React.memo(Plan);
